import { useEffect, useRef, useCallback } from 'react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

/**
 * Hook to lock body scroll when a modal is open.
 * Uses body-scroll-lock library (v3.1.5) for robust cross-browser support including:
 * - iOS Safari rubber-band scrolling prevention
 * - Scrollbar width compensation (prevents layout shift)
 * - Touch event handling on mobile
 *
 * @param {boolean} isLocked - Whether scroll should be locked (default: true)
 * @returns {Object} - { targetRef, unlock }
 *   - targetRef: Ref to attach to the scrollable modal element
 *   - unlock: Function to immediately unlock scroll (call before exit animations)
 */
export function useScrollLock(isLocked = true) {
  const targetRef = useRef(null);
  const isLockedRef = useRef(false);

  // Unlock function for immediate cleanup (before exit animations)
  const unlock = useCallback(() => {
    const target = targetRef.current;
    if (isLockedRef.current && target) {
      enableBodyScroll(target);
      isLockedRef.current = false;
    }
  }, []);

  useEffect(() => {
    const targetElement = targetRef.current;

    if (isLocked && targetElement && !isLockedRef.current) {
      disableBodyScroll(targetElement, {
        reserveScrollBarGap: true,
      });
      isLockedRef.current = true;
    } else if (!isLocked && isLockedRef.current && targetElement) {
      enableBodyScroll(targetElement);
      isLockedRef.current = false;
    }

    // Cleanup only THIS modal's scroll lock on unmount
    return () => {
      if (targetElement && isLockedRef.current) {
        enableBodyScroll(targetElement);
        isLockedRef.current = false;
      }
    };
  }, [isLocked]);

  return { targetRef, unlock };
}

export default useScrollLock;
